import debounce from 'lodash/debounce';
import { analytics } from './analytics';
import {
  ONBOARDING_STEPS,
  DEFAULT_TOTAL_ONBOARDING_STEPS,
} from '@constants/onboarding';
import { FILING_STATUS } from '@shared-types/db-types';

/**
 * Enhanced onboarding analytics utilities for comprehensive PostHog tracking
 */
export class OnboardingAnalytics {
  private static instance: OnboardingAnalytics;

  // Debounced version of the track method specifically for 'change' events
  private debouncedTrackFieldChange = debounce(
    (
      stepNumber: number,
      stepName: string,
      fieldName: string,
      value?: unknown
    ) => {
      analytics.track('onboarding_field_interaction', {
        step_number: stepNumber,
        step_name: stepName,
        field_name: fieldName,
        interaction_type: 'change',
        has_value: value !== undefined && value !== null && value !== '',
        value_type: typeof value,
      });
    },
    2000 // Wait 2 seconds after user stops typing
  );

  static getInstance(): OnboardingAnalytics {
    if (!OnboardingAnalytics.instance) {
      OnboardingAnalytics.instance = new OnboardingAnalytics();
    }
    return OnboardingAnalytics.instance;
  }

  /**
   * Track when onboarding starts
   */
  trackOnboardingStart(
    totalSteps: number = DEFAULT_TOTAL_ONBOARDING_STEPS
  ): void {
    analytics.trackOnboardingStart(totalSteps);
    analytics.track('onboarding_session_started', {
      total_steps: totalSteps,
      user_agent: navigator.userAgent,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track when a step is viewed/entered
   */
  trackStepView(
    stepNumber: number,
    totalSteps: number = DEFAULT_TOTAL_ONBOARDING_STEPS
  ): void {
    const stepName =
      ONBOARDING_STEPS[stepNumber as keyof typeof ONBOARDING_STEPS];

    analytics.track('onboarding_step_viewed', {
      step_number: stepNumber,
      step_name: stepName,
      total_steps: totalSteps,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track step completion with detailed context
   */
  trackStepCompletion(
    stepNumber: number,
    stepData: Record<string, unknown>,
    totalSteps: number = DEFAULT_TOTAL_ONBOARDING_STEPS,
    timeSpentOnStep?: number
  ): void {
    const stepName =
      ONBOARDING_STEPS[stepNumber as keyof typeof ONBOARDING_STEPS];

    analytics.trackOnboardingStep(stepNumber, stepData, totalSteps);

    // Track additional step context
    analytics.track('onboarding_step_interaction', {
      step_number: stepNumber,
      step_name: stepName,
      time_spent_seconds: timeSpentOnStep,
      has_step_data: Object.keys(stepData).length > 0,
      step_data_keys: Object.keys(stepData),
    });
  }

  /**
   * Track specific field interactions
   */
  trackFieldInteraction(
    stepNumber: number,
    fieldName: string,
    interactionType: 'focus' | 'blur' | 'change' | 'validation_error',
    value?: unknown
  ): void {
    const stepName =
      ONBOARDING_STEPS[stepNumber as keyof typeof ONBOARDING_STEPS];

    // Use debounced version for 'change' events to prevent excessive logging
    if (interactionType === 'change') {
      this.debouncedTrackFieldChange(stepNumber, stepName, fieldName, value);
      return;
    }

    // For other interaction types (focus, blur, validation_error), track immediately
    analytics.track('onboarding_field_interaction', {
      step_number: stepNumber,
      step_name: stepName,
      field_name: fieldName,
      interaction_type: interactionType,
      has_value: value !== undefined && value !== null && value !== '',
      value_type: typeof value,
    });
  }

  /**
   * Track tax savings calculations
   */
  trackTaxSavingsCalculation(
    annualIncome: number,
    estimatedSavings: number,
    filingStatus: FILING_STATUS,
    taxState: string
  ): void {
    analytics.trackTaxSavingsCalculated(estimatedSavings, {
      annual_income: annualIncome,
      filing_status: filingStatus,
      tax_state: taxState,
    });

    analytics.track('tax_savings_widget_interaction', {
      annual_income_range: this.getIncomeRange(annualIncome),
      estimated_savings: estimatedSavings,
      savings_percentage: Math.round((estimatedSavings / annualIncome) * 100),
      filing_status: filingStatus,
      tax_state: taxState,
    });
  }

  /**
   * Track form validation errors
   */
  trackValidationError(
    stepNumber: number,
    fieldName: string,
    errorMessage: string,
    inputValue?: unknown
  ): void {
    const stepName =
      ONBOARDING_STEPS[stepNumber as keyof typeof ONBOARDING_STEPS];

    analytics.trackFormValidationError(fieldName, stepName, errorMessage);

    analytics.track('onboarding_validation_error', {
      step_number: stepNumber,
      step_name: stepName,
      field_name: fieldName,
      error_message: errorMessage,
      input_value_type: typeof inputValue,
      input_value_length:
        typeof inputValue === 'string' ? inputValue.length : undefined,
    });
  }

  /**
   * Track back navigation
   */
  trackBackNavigation(fromStep: number, toStep: number): void {
    analytics.trackOnboardingBackNavigation(fromStep, toStep);

    analytics.track('onboarding_navigation_back', {
      from_step: fromStep,
      to_step: toStep,
      steps_backwards: fromStep - toStep,
      from_step_name:
        ONBOARDING_STEPS[fromStep as keyof typeof ONBOARDING_STEPS],
      to_step_name: ONBOARDING_STEPS[toStep as keyof typeof ONBOARDING_STEPS],
    });
  }

  /**
   * Track onboarding abandonment
   */
  trackAbandonment(
    currentStep: number,
    totalSteps: number,
    timeSpent?: number,
    completedSteps?: string[]
  ): void {
    analytics.trackOnboardingAbandonment(currentStep, totalSteps);

    analytics.track('onboarding_session_abandoned', {
      abandoned_at_step: currentStep,
      progress_percentage: Math.round((currentStep / totalSteps) * 100),
      time_spent_total_seconds: timeSpent,
      completed_steps: completedSteps,
      completed_steps_count: completedSteps?.length || 0,
    });
  }

  /**
   * Track successful onboarding completion
   */
  trackCompletion(
    totalSteps: number,
    completionData: Record<string, unknown>,
    totalTimeSpent?: number
  ): void {
    analytics.trackOnboardingCompletion(totalSteps, completionData);

    analytics.track('onboarding_flow_completed_success', {
      total_steps: totalSteps,
      completion_time_seconds: totalTimeSpent,
      completion_time_minutes: totalTimeSpent
        ? Math.round(totalTimeSpent / 60)
        : undefined,
      annual_income_range: completionData.annualIncome
        ? this.getIncomeRange(completionData.annualIncome as number)
        : undefined,
      risk_preference: completionData.riskPreference,
      filing_status: completionData.filingStatus,
    });
  }

  /**
   * Track user interaction with help/info elements
   */
  trackHelpInteraction(
    stepNumber: number,
    helpType: 'tooltip' | 'info_icon' | 'help_text'
  ): void {
    const stepName =
      ONBOARDING_STEPS[stepNumber as keyof typeof ONBOARDING_STEPS];

    analytics.track('onboarding_help_interaction', {
      step_number: stepNumber,
      step_name: stepName,
      help_type: helpType,
    });
  }

  /**
   * Track option card selections (for multiple choice steps)
   */
  trackOptionSelection(
    stepNumber: number,
    optionId: string,
    optionTitle: string,
    isSelected: boolean
  ): void {
    const stepName =
      ONBOARDING_STEPS[stepNumber as keyof typeof ONBOARDING_STEPS];

    analytics.track('onboarding_option_selected', {
      step_number: stepNumber,
      step_name: stepName,
      option_id: optionId,
      option_title: optionTitle,
      is_selected: isSelected,
      action: isSelected ? 'select' : 'deselect',
    });
  }

  /**
   * Helper method to categorize income ranges for analytics
   */
  private getIncomeRange(income: number): string {
    if (income < 50000) return 'under_50k';
    if (income < 100000) return '50k_to_100k';
    if (income < 200000) return '100k_to_200k';
    if (income < 500000) return '200k_to_500k';
    return 'over_500k';
  }
}

// Export singleton instance
export const onboardingAnalytics = OnboardingAnalytics.getInstance();
